import { useState, useMemo, useEffect } from 'react';
import { Button, Box, Typography, Autocomplete, TextField } from '@mui/material';
import { Plus } from 'lucide-react';
import DataTable from '../../common/DataTable/index';
import Search from '../../common/Search';
import CommonDialog from '../../common/CommonDialog';
import AddOrgType from './AddGlobalType';
import ExportExcel from '../../common/ExportExcel';
import { useMasterData, useResponsive, isAddAllowed, userScreenAccess } from '../../../../utils';
import { fetchGlobalTypes } from '../../../../apis/Masters/GlobalType';
import { statusOption } from '../../../../constants/constant';
import { FilterFieldSx, DropDownSX } from '../../../../constants/style';

let _editRowHandler = null;

const headers = [
  {
    label: 'Global Type',
    key: 'globalTypeName',
    minWidth: 80,
    sortable: true,
    align: 'left',
    resizable: true
  },
  {
    label: 'Sort Order',
    key: 'sortOrder',
    minWidth: 180,
    sortable: true
  },
  {
    label: 'Status',
    key: 'isActive',
    minWidth: 140,
    align: 'center',
    sortable: true,
    render: (val) => {
      const statusText = val ? 'Active' : 'Inactive';
      const badgeClass =
        {
          true: 'bg-success-subtle text-success',
          false: 'bg-danger-subtle text-danger'
        }[val] || 'bg-secondary';
      return <span className={`${badgeClass} px-2 py-1 fw-semibold`}>{statusText}</span>;
    }
  },
  {
    label: 'Actions',
    key: '_actions',
    minWidth: 80,
    sortable: false,
    align: 'center',
    render: (_val, row) => (
      <Button
        size="small"
        variant="outlined"
        onClick={(e) => {
          e.stopPropagation();
          _editRowHandler?.(row);
        }}
        sx={{
          fontSize: '0.7rem',
          textTransform: 'none',
          borderRadius: 1.5,
          py: 0.3,
          px: 1.2,
          borderColor: 'divider',
          color: 'text.secondary',
          '&:hover': { borderColor: 'primary.main', color: 'primary.main' }
        }}
      >
        Edit
      </Button>
    )
  }
];

const OrgList = () => {
  const [orgList, setOrgList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null); // null = Add mode
  const [tableHeaders, setTableHeaders] = useState(headers);
  const [statusGUID, setStatusGUID] = useState(1);
  // const [tableData, setTableData] = useState([]);
  const { isMobile } = useResponsive();

  const masterData = useMasterData();
  const userAccess = userScreenAccess();

  const fetchOrgListResponse = async (status) => {
    if (status === undefined) status = statusGUID; // use current state if not provided
    if (masterData?.id) {
      setIsLoading(true);
      try {
        const orderList = await fetchGlobalTypes(masterData?.id, '', status);
        setOrgList(orderList);
      } catch (err) {
        console.log('Error fetching orderList:', err);
      } finally {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchOrgListResponse(statusGUID);
  }, [masterData?.id]);

  // ── Wire module-level handler for the Actions column ─────────────────────
  _editRowHandler = (row) => {
    if (!userAccess.hasEditPermission) return;
    setSelectedUser(row);
    setOpenDialog(true);
  };

  // ── Open Add dialog ───────────────────────────────────────────────────────
  const handleAddUser = () => {
    if (!userAccess.hasAddPermission) return;
    setSelectedUser(null);
    setOpenDialog(true);
  };

  // ── Close dialog ──────────────────────────────────────────────────────────
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
  };

  const handleStatusChange = (event, value) => {
    const status = value?.value;
    setStatusGUID(status);
    fetchOrgListResponse(status);
  };

  // const searchData = async (query) => {
  //   setIsLoading(true);
  //   const searchData = await fetchSearchData(masterData?.id, query);
  //   setTableData(searchData);
  //   setIsLoading(false);
  // };

  // ── Filtered data ─────────────────────────────────────────────────────────
  const filteredData = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return orgList;
    return orgList.filter((row) => {
      // Normalize values: convert boolean isActive → display text so
      // searching "Active" or "Inactive" / "InActive" works correctly
      const normalizedValues = Object.entries(row).map(([key, val]) => {
        if (key === 'isActive')
          return typeof val === 'boolean'
            ? val
              ? 'active'
              : 'inactive'
            : String(val).toLowerCase();
        return String(val).toLowerCase();
      });
      return normalizedValues.some((v) => v.includes(q));
    });
  }, [searchQuery, orgList]);

  // useEffect(() => {
  //   const q = searchQuery.trim().toLowerCase();
  //   if (!q) return setTableData(orgList);
  //   searchData(q);
  // }, [searchQuery, orgList]);

  useEffect(() => {
    if (masterData?.id) {
      setTableHeaders((prevHeaders) =>
        prevHeaders.map((header) =>
          header.key === 'globalTypeName' ? { ...header, label: masterData.title } : header
        )
      );
    }
  }, [masterData.id]);

  return (
    <div className="w-100">
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' },
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 3
        }}
      >
        <Typography variant="h6" fontWeight="bold" color="text.primary">
          {masterData?.title || 'Global Type'} List
        </Typography>
      </Box>
      {/* ── Toolbar ───────────────────────────────────────────────────── */}
      <div className="d-flex align-items-center justify-content-between mb-3 gap-2 flex-wrap">
        <Autocomplete
          size="small"
          options={statusOption}
          value={statusOption.find((u) => u.value === statusGUID) || null}
          onChange={handleStatusChange}
          disableClearable={true}
          getOptionLabel={(option) => option?.label || ''}
          isOptionEqualToValue={(option, value) => option?.value === value?.value}
          sx={{ width: '30%' }} // adjust width here
          slotProps={{
            popper: {
              sx: DropDownSX
            }
          }}
          renderInput={(params) => (
            <TextField {...params} sx={FilterFieldSx} placeholder="Select Status" />
          )}
        />
        <Search
          width={isMobile ? '170px' : '280px'}
          height="36px"
          placeholder="Search by name, role or status..."
          debounce={250}
          onSearch={setSearchQuery}
          onClear={() => setSearchQuery('')}
        />

        <div className="d-flex align-items-center gap-2">
          <ExportExcel
            data={orgList}
            fileName={`${masterData?.title || 'GlobalType'}_${new Date()
              .toISOString()
              .slice(0, 10)}.xlsx`}
            sheetName={masterData?.title || 'Global Type'}
            buttonLabel="Excel"
            exportKeys={tableHeaders.filter((h) => h.key !== '_actions').map((h) => h.key)}
            exportHeaders={tableHeaders.filter((h) => h.key !== '_actions').map((h) => h.label)}
          />
          <Button
            variant="contained"
            size="small"
            startIcon={<Plus size={15} />}
            onClick={handleAddUser}
            sx={{
              background: 'var(--primary, #36c95f)',
              '&:hover': { background: 'var(--primary-dark, #27ae60)' },
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              px: 2,
              py: 0.8,
              fontSize: '0.83rem',
              boxShadow: '0 4px 12px rgba(46,204,113,0.28)',
              display: userAccess.hasAddPermission ? 'inline-flex' : 'none'
            }}
          >
            Add
          </Button>
        </div>
      </div>

      {/* ── Data Table ────────────────────────────────────────────────── */}
      <DataTable
        headers={tableHeaders}
        data={filteredData}
        isLoading={isLoading}
        selectable={false}
        // onRowClick={(row) => _editRowHandler?.(row)}
        emptyMessage={
          searchQuery ? `No results for "${searchQuery}"` : 'No users found in the system.'
        }
        enablePagination={true}
        zebra={false}
        onRowClick={_editRowHandler}
      />

      {/* ── Add / Edit Dialog ─────────────────────────────────────────── */}
      <CommonDialog
        open={openDialog}
        onClose={handleCloseDialog}
        title={
          selectedUser
            ? `Edit ${masterData?.title} — ${selectedUser.globalTypeName}`
            : `Add New ${masterData?.title}`
        }
        maxWidth="sm"
        persistent={true}
        fullScreen={isMobile ? true : false}
      >
        <AddOrgType
          masterData={masterData}
          userData={selectedUser}
          userAccess={userAccess}
          onRefresh={fetchOrgListResponse}
          onClose={handleCloseDialog}
        />
      </CommonDialog>
    </div>
  );
};

export default OrgList;
